/**
 * Chile con la tarifa de cada region escrita encima (ADM-10).
 *
 * Las etiquetas son el control: botones de verdad, en orden de norte a sur,
 * que abren la region. El dibujo es apoyo visual (`aria-hidden`) y un clic
 * sobre una region hace lo mismo que su etiqueta, para quien apunta al mapa.
 *
 * En ancho de escritorio las etiquetas se reparten en dos filas, arriba y
 * abajo del pais, alternadas: las regiones del centro miden pocos pixeles y
 * una etiqueta encima de cada una se pisarian. Una guia une cada etiqueta con
 * su region. Las filas y el dibujo comparten el ancho, asi que la columna de
 * una etiqueta se traduce directo a coordenadas del `viewBox`. En un
 * contenedor angosto el mapa no se lee y queda solo la lista.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';

import type { TarifaRegionAdmin } from '@bandu/compartido';

import {
  MAPA_CHILE_ALTO,
  MAPA_CHILE_ANCHO,
  NOMBRE_CORTO_REGION,
  REGIONES_MAPA,
  ZONA_SIN_DEMARCAR,
} from '../../configuracion/mapa-chile';
import { pesos } from '../../utilidades/formato';

/** Alto, en unidades del mapa, del tramo donde corren las guias. */
const GUIA = 46;
/** Cuanto baja la guia en vertical antes de doblar hacia su region. */
const CODO = 14;
const COLUMNAS = 8;

type EstadoRegion = 'con-precio' | 'sin-precio' | 'apagada';

function estadoRegion(region: TarifaRegionAdmin): EstadoRegion {
  if (!region.activa) return 'apagada';
  return region.costo === 0 ? 'sin-precio' : 'con-precio';
}

interface MapaDespachoProps {
  regiones: TarifaRegionAdmin[];
  alElegir: (codigo: string) => void;
}

export function MapaDespacho({ regiones, alElegir }: MapaDespachoProps) {
  const [resaltada, setResaltada] = useState<string | null>(null);

  const trazados = new Map(REGIONES_MAPA.map((region) => [region.codigo, region]));
  const anchoColumna = MAPA_CHILE_ANCHO / COLUMNAS;
  const altoTotal = MAPA_CHILE_ALTO + 2 * GUIA;

  return (
    <div className="mapa-despacho">
      <div className="mapa-despacho-grilla">
        <ul className="mapa-despacho-etiquetas" aria-label="Tarifa de despacho por region">
          {regiones.map((region, indice) => {
            const estado = estadoRegion(region);
            const posicion = {
              '--fila': indice % 2 === 0 ? 1 : 3,
              '--col': Math.floor(indice / 2) + 1,
            } as CSSProperties;

            return (
              <li key={region.codigo} style={posicion}>
                <button
                  type="button"
                  className={[
                    'mapa-etiqueta',
                    estado,
                    resaltada === region.codigo ? 'resaltada' : '',
                  ].join(' ')}
                  onClick={() => {
                    alElegir(region.codigo);
                  }}
                  onMouseEnter={() => {
                    setResaltada(region.codigo);
                  }}
                  onMouseLeave={() => {
                    setResaltada(null);
                  }}
                  onFocus={() => {
                    setResaltada(region.codigo);
                  }}
                  onBlur={() => {
                    setResaltada(null);
                  }}
                >
                  <span className="mapa-etiqueta-region">
                    <span className="solo-lectores">{region.nombre}</span>
                    <span aria-hidden="true">
                      {NOMBRE_CORTO_REGION[region.codigo] ?? region.nombre}
                    </span>
                    {region.excepciones > 0 ? (
                      <span
                        className="contador-comunas"
                        title={`${region.excepciones} ${region.excepciones === 1 ? 'comuna' : 'comunas'} con precio propio`}
                      >
                        <span aria-hidden="true">{region.excepciones}</span>
                        <span className="solo-lectores">
                          , {region.excepciones} {region.excepciones === 1 ? 'comuna' : 'comunas'}{' '}
                          con precio propio
                        </span>
                      </span>
                    ) : null}
                  </span>
                  <span className="mapa-etiqueta-precio">
                    {estado === 'apagada' ? (
                      'Sin despacho'
                    ) : estado === 'sin-precio' ? (
                      <>
                        <span aria-hidden="true">—</span>
                        <span className="solo-lectores">Sin precio</span>
                      </>
                    ) : (
                      pesos(region.costo)
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <svg
          className="mapa-despacho-dibujo"
          viewBox={`0 0 ${MAPA_CHILE_ANCHO} ${altoTotal}`}
          aria-hidden="true"
        >
          <g className="mapa-guias">
            {regiones.map((region, indice) => {
              const trazado = trazados.get(region.codigo);
              if (trazado === undefined) return null;

              const arriba = indice % 2 === 0;
              const x = (Math.floor(indice / 2) + 0.5) * anchoColumna;
              const y = arriba ? 0 : altoTotal;
              const codo = arriba ? CODO : altoTotal - CODO;
              const [anclaX, anclaY] = trazado.ancla;

              return (
                <polyline
                  key={region.codigo}
                  className={resaltada === region.codigo ? 'resaltada' : undefined}
                  points={`${x},${y} ${x},${codo} ${anclaX},${anclaY + GUIA}`}
                />
              );
            })}
          </g>

          <g transform={`translate(0 ${GUIA})`}>
            <path className="mapa-zona-neutra" d={ZONA_SIN_DEMARCAR} />
            {regiones.map((region) => {
              const trazado = trazados.get(region.codigo);
              if (trazado === undefined) return null;

              return (
                <path
                  key={region.codigo}
                  className={[
                    'mapa-region',
                    estadoRegion(region),
                    resaltada === region.codigo ? 'resaltada' : '',
                  ].join(' ')}
                  d={trazado.d}
                  onClick={() => {
                    alElegir(region.codigo);
                  }}
                  onMouseEnter={() => {
                    setResaltada(region.codigo);
                  }}
                  onMouseLeave={() => {
                    setResaltada(null);
                  }}
                />
              );
            })}
            {regiones.map((region) => {
              const trazado = trazados.get(region.codigo);
              if (trazado === undefined) return null;
              const [anclaX, anclaY] = trazado.ancla;

              return (
                <circle
                  key={region.codigo}
                  className={resaltada === region.codigo ? 'mapa-ancla resaltada' : 'mapa-ancla'}
                  cx={anclaX}
                  cy={anclaY}
                  r={2.6}
                />
              );
            })}
          </g>
        </svg>
      </div>

      <ul className="mapa-leyenda" aria-label="Que significa cada color">
        <li>
          <span className="muestra con-precio" />
          Activa con tarifa
        </li>
        <li>
          <span className="muestra sin-precio" />
          Activa sin precio: se ofrece a $0
        </li>
        <li>
          <span className="muestra apagada" />
          Sin despacho: no se puede elegir en el checkout
        </li>
        <li>
          <span className="contador-comunas" aria-hidden="true">
            N
          </span>
          Comunas con precio propio en la region
        </li>
      </ul>
    </div>
  );
}
